'use client';
import { useState, Fragment, useRef, useEffect } from 'react';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { components } from './PortableTextComponents';
import {
  FeatureProductCard,
  FeatureProductCardDetail,
} from '@/components/shared/cards';
import { FeatureCardType } from '@/components/shared/cards/FeatureProductCard';
import { m, useInView, AnimatePresence } from 'framer-motion';
import { moveUp } from '@/assets/framer/animations';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Scrollbar } from 'swiper/modules';
import { testIds } from '@/constants/testIds';
import {
  SliderArrowLeft,
  SliderArrowRight,
} from '../shared/svg-icons/SliderArrow';
import IKTopDividerWave from '../shared/patterns/IKTopDividerWave';
import IKBottomDividerWave from '../shared/patterns/IKBottomDividerWave';
import Image from 'next/image';
import { MenuItemLink } from '@/types/types';
import styles from '@components/sections/industry-personalization/IndustryStickyNav.module.css';
import { IKButton } from '../shared/material';
import { useMatchMedia } from '@/lib/matchMedia';
import { useSearchParams } from 'next/navigation';

export type CategoryCTA = {
  text: string;
  link: MenuItemLink;
};

export type SolutionsEcosystemCategory = {
  _key: string;
  title: string;
  description?: PortableTextBlock[];
  cards: FeatureCardType[];
  categoryCTA?: CategoryCTA;
  solutionsIcon?: {
    url: string;
  };
};

export type SolutionsEcosystemData = {
  heading?: PortableTextBlock[];
  subHeading?: PortableTextBlock[];
  type?: 'feature' | 'details';
  categories: SolutionsEcosystemCategory[];
};

export type SolutionsEcosystemProps = {
  data?: SolutionsEcosystemData;
};

export default function SolutionsEcosystem({ data }: SolutionsEcosystemProps) {
  const categories = data?.categories || [];
  const searchParams = useSearchParams();
  const sectionRef = useRef<HTMLElement>(null);

  // Get the 'solution' query param and find the matching category
  const solutionParam = searchParams?.get('solution');
  const initialCategoryKey =
    categories.find(
      (cat) =>
        cat.title.toLowerCase().replace(/\s+/g, '') ===
        (solutionParam || '').toLowerCase().replace(/\s+/g, '')
    )?._key ||
    categories[0]?._key ||
    '0';

  const [currentCategory, setCurrentCategory] =
    useState<string>(initialCategoryKey);
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState<'up' | 'down'>('up');

  const type = data?.type || 'feature';
  const heading = data?.heading;
  const subHeading = data?.subHeading;
  const cards =
    data?.categories.find((category) => category._key === currentCategory)
      ?.cards || [];

  const currentCategoryObj = data?.categories.find(
    (category) => category._key === currentCategory
  );

  const currentCTA = currentCategoryObj?.categoryCTA;

  const headingRef = useRef(null);
  const isInView = useInView(headingRef, { amount: 0.4, once: true });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const slideClass = `${type == 'details' ? '!flex flex-col !h-full' : '!w-[unset]'} 2xl:max-w-[450px] lg:max-w-[420px] sm:max-w-[350px] max-w-[280px]`;
  const paginationButtonClass = `disabled:opacity-40 cursor-pointer [&_path]:fill-black fill-black h-12 w-12 shadow-lg  rounded-full bg-yellow hover:bg-yellow-500 flex items-center justify-center hover:scale-105 p-2.5`;
  const leftButton = `${paginationButtonClass}  swiper-button-outline-left`;
  const rightButton = `${paginationButtonClass} swiper-button-outline-right`;

  const isMobile = useMatchMedia('(max-width: 1023px)', true);

  // Go to first slide when category changes
  useEffect(() => {
    if (swiperInstance && cards.length > 0) {
      swiperInstance.slideTo(0);
    }
  }, [currentCategory, swiperInstance]);

  // Scroll current tab into view on mount and category change(mobile nav)
  useEffect(() => {
    if (!isMobile) return;

    const currentIndex = categories.findIndex(
      (category) => category._key === currentCategory
    );

    if (currentIndex !== -1 && tabRefs.current[currentIndex]) {
      const tab = tabRefs.current[currentIndex];
      const container = tab?.parentElement;

      if (tab && container) {
        requestAnimationFrame(() => {
          const padding = 20;
          const tabLeft = tab.offsetLeft;
          const scrollTo = tabLeft - padding;

          container.scrollTo({
            left: scrollTo,
            behavior: 'smooth',
          });
        });
      }
    }
  }, [currentCategory, categories, isMobile]);

  // Scroll to section if solution param is present
  useEffect(() => {
    if (solutionParam && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [solutionParam]);

  // Helper to get category index by key
  const getCategoryIndex = (key: string) =>
    categories.findIndex((category) => category._key === key);

  const handleCategoryChange = (newKey: string) => {
    const currentIdx = getCategoryIndex(currentCategory);
    const nextIdx = getCategoryIndex(newKey);

    if (nextIdx === -1 || currentIdx === -1) {
      setDirection('up');
    } else {
      setDirection(nextIdx > currentIdx ? 'down' : 'up');
    }
    setCurrentCategory(newKey);
  };

  return (
    <section
      ref={sectionRef}
      className='relative z-40 bg-charcoal bg-gradient-to-b pt-5 sm:pt-0 md:pb-16'
    >
      <IKTopDividerWave />

      <div className='flex w-full flex-col overflow-hidden bg-charcoal py-12 text-white'>
        <div className='container-padding container flex flex-col gap-4'>
          <div className='lg:max-w-[50vw]'>
            <m.h2
              ref={headingRef}
              className='heading-xl relative z-10 w-full font-display font-medium tracking-tighter text-yellow-150 sm:max-w-4xl [&_strong]:text-yellow'
            >
              {heading && (
                <PortableText components={components} value={heading} />
              )}
            </m.h2>
          </div>
          <m.p className='heading-xs hidden pr-4 text-white sm:pr-0 md:pr-5 lg:block lg:pr-10 xl:pr-0 [&_strong]:text-yellow'>
            {subHeading && (
              <PortableText components={components} value={subHeading} />
            )}
          </m.p>
        </div>

        {/* Mobile category navbar */}
        <div className='mb-4 mt-8 lg:hidden'>
          <div
            className={`container-padding container flex gap-2 overflow-x-auto ${styles.scrollHide}`}
          >
            {categories.map((category, index) => (
              <button
                key={category._key}
                onClick={() => handleCategoryChange(category._key)}
                ref={(el) => (tabRefs.current[index] = el)}
                className={`heading-xs flex items-center justify-start gap-2 whitespace-nowrap rounded-md p-2 text-left text-sm text-charcoal ${
                  currentCategory === category._key
                    ? 'bg-yellow'
                    : 'bg-yellow-150'
                }`}
              >
                <p className='heading-xs whitespace-nowrap'>{category.title}</p>
              </button>
            ))}
          </div>

          {categories.map((category) => (
            <div className='container-padding container' key={category._key}>
              {currentCategory === category._key && (
                <m.p className='mt-4 text-lg text-white [&_strong]:text-yellow'>
                  {category.description && (
                    <PortableText
                      components={components}
                      value={category.description}
                    />
                  )}
                </m.p>
              )}
            </div>
          ))}
        </div>
        {/* Mobile Category Nav End */}

        <div className='container-padding container gap-8'>
          {cards && cards.length > 0 && (
            <div className='relative'>
              <div className='hidden items-center justify-between lg:flex lg:min-h-[80px]'>
                {activeSlide !== 0 && (
                  <button
                    onClick={() => swiperInstance?.slideTo(0)}
                    className='flex w-64 items-center justify-center gap-2 rounded-lg border border-charcoal-600 bg-charcoal-800 px-2 py-2'
                  >
                    <div className='flex h-8 w-8 items-center justify-center rounded-full bg-yellow fill-black [&_path]:fill-black'>
                      <SliderArrowLeft />
                    </div>
                    Other solutions
                  </button>
                )}
                <div className='flex w-full items-center justify-end gap-4'>
                  <button
                    ref={prevRef}
                    className={leftButton}
                    aria-label='Previous slide'
                  >
                    <SliderArrowLeft />
                  </button>
                  <button
                    ref={nextRef}
                    className={rightButton}
                    aria-label='Next slide'
                  >
                    <SliderArrowRight />
                  </button>
                </div>
              </div>
              <m.div
                className='relative z-10'
                initial='outOfView'
                animate={isInView ? 'visible' : 'outOfView'}
                variants={moveUp}
              >
                <Swiper
                  className='!flex !h-full flex-col !overflow-visible [&>.swiper-wrapper]:my-2'
                  modules={[Navigation, Scrollbar, A11y]}
                  breakpoints={{
                    0: { slidesPerView: 'auto', spaceBetween: 0 },
                    425: { slidesPerView: 'auto', spaceBetween: 0 },
                    765: { slidesPerView: 'auto', spaceBetween: 0 },
                    980: { slidesPerView: 'auto', spaceBetween: 0 },
                  }}
                  navigation={{
                    prevEl: prevRef.current,
                    nextEl: nextRef.current,
                    disabledClass: 'disabled',
                  }}
                  onSwiper={setSwiperInstance}
                  onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
                >
                  {!isMobile && (
                    <SwiperSlide
                      className={`hidden max-w-[280px] pr-8 sm:max-w-[350px] lg:block lg:max-w-[350px] 2xl:max-w-[350px]`}
                      key='category-menu'
                    >
                      <AnimatePresence initial={false}>
                        {activeSlide === 0 && (
                          <m.div
                            key='accordion'
                            initial={{ maxHeight: 0, opacity: 0 }}
                            animate={{ maxHeight: 1000, opacity: 1 }}
                            exit={{ maxHeight: 0, opacity: 0 }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                            className='overflow-hidden'
                          >
                            <div className='flex flex-col gap-4'>
                              {categories.map((category) => (
                                <div className='w-[300px]' key={category._key}>
                                  <button
                                    onClick={() =>
                                      handleCategoryChange(category._key)
                                    }
                                    className={`flex w-full cursor-pointer items-center justify-start gap-2 rounded-md p-2 text-left text-xl text-charcoal ${
                                      currentCategory === category._key
                                        ? 'bg-yellow'
                                        : 'bg-yellow-150'
                                    }`}
                                  >
                                    {category.solutionsIcon &&
                                      category.solutionsIcon.url && (
                                        <div
                                          className={`flex h-8 w-8 items-center justify-center rounded-full bg-charcoal`}
                                        >
                                          <Image
                                            src={category.solutionsIcon.url}
                                            alt={category.title}
                                            width={32}
                                            height={32}
                                            className={`h-5 w-5 object-contain transition duration-200 ${
                                              currentCategory === category._key
                                                ? ''
                                                : 'brightness-[1.05] hue-rotate-[10deg] saturate-[0.5] sepia-[0.4] filter'
                                            }`}
                                          />
                                        </div>
                                      )}
                                    <p>{category.title}</p>
                                  </button>

                                  {currentCategory === category._key && (
                                    <AnimatePresence mode='wait'>
                                      <m.p
                                        key={category._key}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.3 }}
                                        className='mt-4 text-lg text-white [&_strong]:text-yellow'
                                      >
                                        {category.description && (
                                          <PortableText
                                            components={components}
                                            value={category.description}
                                          />
                                        )}
                                      </m.p>
                                    </AnimatePresence>
                                  )}
                                </div>
                              ))}
                            </div>
                          </m.div>
                        )}
                      </AnimatePresence>
                    </SwiperSlide>
                  )}

                  {cards.map((card, index) => (
                    <SwiperSlide
                      className={`pr-8 ${slideClass}`}
                      data-test-qa={`${testIds.purchaseJourneyCard}-${index}`}
                      key={`feature-product-card-${type}-${index}`}
                    >
                      <div className='flex h-full flex-col justify-between'>
                        <Fragment>
                          <AnimatePresence mode='wait'>
                            <m.div
                              key={currentCategory}
                              initial={{
                                opacity: 0,
                                y: direction === 'up' ? -100 : 100,
                              }}
                              animate={{
                                opacity: 1,
                                y: 0,
                                transition: { duration: 0.2 },
                              }}
                              exit={{
                                opacity: 0,
                                y: direction === 'up' ? 100 : -100,
                                transition: { duration: 0.2 },
                              }}
                            >
                              {type === 'details' ? (
                                <FeatureProductCardDetail
                                  index={index}
                                  card={card}
                                />
                              ) : (
                                <FeatureProductCard index={index} card={card} />
                              )}
                            </m.div>
                          </AnimatePresence>
                        </Fragment>

                        {index === 0 &&
                          currentCTA?.link &&
                          currentCTA?.text && (
                            <div className='mt-10 hidden lg:block'>
                              <IKButton
                                link={currentCTA.link}
                                className='hover: flex items-center justify-center gap-3 bg-charcoal-900 text-white shadow-none hover:bg-charcoal-800 hover:shadow-none'
                              >
                                {currentCTA.text}{' '}
                                <div className='flex h-8 w-8 items-center justify-center rounded-full bg-yellow fill-black [&_path]:fill-black'>
                                  <SliderArrowRight />
                                </div>
                              </IKButton>
                            </div>
                          )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </m.div>

              {currentCTA?.link && currentCTA?.text && (
                <div className='mt-4 w-full lg:hidden'>
                  <IKButton
                    link={currentCTA.link}
                    className='hover: flex w-full items-center justify-center gap-3 border border-charcoal-600 bg-charcoal-900 text-white shadow-none hover:bg-charcoal-800 hover:shadow-none'
                  >
                    {currentCTA.text}
                    <div className='flex h-8 w-8 items-center justify-center rounded-full bg-yellow fill-black [&_path]:fill-black'>
                      <SliderArrowRight />
                    </div>
                  </IKButton>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <IKBottomDividerWave />
    </section>
  );
}
